import { ErrorPage, PageHeader, SkeletonText, Stack, VisuallyHidden } from '@hkdevs/design-system';
import { useEffect, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { reportError } from '../observability/errorTracking';
import { PAGES, type PageKey } from './pages';

/**
 * A signed-in page: its header over its body. The title and subtitle come from PAGES
 * unless given (a nested page: a check's date).
 */
export function PageLayout({
  page,
  title,
  subtitle,
  breadcrumb,
  actions,
  children,
}: {
  page: PageKey;
  title?: string;
  subtitle?: string;
  breadcrumb?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack gap={5}>
      <PageHeader
        title={title ?? PAGES[page].title}
        subtitle={subtitle ?? PAGES[page].subtitle}
        breadcrumb={breadcrumb}
        actions={actions}
      />
      {children}
    </Stack>
  );
}

/** While a signed-in page loads: the page's shape, not a spinner. */
export function PageSkeleton() {
  return (
    <Stack gap={5} aria-busy="true">
      <VisuallyHidden>Loading…</VisuallyHidden>
      <SkeletonText lines={2} />
      <SkeletonText lines={6} />
    </Stack>
  );
}

/** 403 and 404 inside the shell. Go to home goes to the user's default route. */
export function Forbidden({ home }: { home: string }) {
  return <ErrorPage variant="forbidden" homeHref={home} />;
}

export function NotFound({ home }: { home: string }) {
  return <ErrorPage variant="notFound" homeHref={home} />;
}

/** An unexpected error, with the request's ID for support when the API gave one. */
export function UnexpectedError({
  error,
  home = '/',
  standalone = false,
}: {
  error: unknown;
  home?: string;
  standalone?: boolean;
}) {
  const reference = error instanceof ApiError ? error.problem.requestId : undefined;
  useEffect(() => reportError(error), [error]);
  return (
    <ErrorPage
      variant="generic"
      homeHref={home}
      standalone={standalone}
      {...(reference ? { reference } : {})}
    />
  );
}
